import {useHistoryAssetsByIdQuery, useHistoryAssetsQuery} from '@domain/assets'
import {
  getMillProductionTimeForMaterial,
  useMaterialsStockLevelsQuery,
  useMaterialStorageDataFromHistoryQuery
} from '@domain/material-storage'
import {Material, useMaterialsRecipesById} from '@domain/materials'
import {
  MomentRange,
  usePlanningChartScheduleData,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

type OptimizerStockMillETAIndicatorProps = {
  plantCode: string
  material: Material
}

export const OptimizerStockMillETAIndicator = ({
  plantCode,
  material
}: OptimizerStockMillETAIndicatorProps) => {
  const materialId = material.id

  const {
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)

  const {startOfPlan, endOfPlan, endOfChart} = usePlanningChartStartEnd()
  const planTimeFrame: MomentRange = [startOfPlan, endOfPlan]

  const {scheduleId} = usePlanningChartScheduleData()

  const {data: stockLevelsData} = useMaterialsStockLevelsQuery({
    pointAtTime: endOfChart,
    planRange: [startOfPlan, endOfPlan],
    scheduleId: scheduleId
  })

  const stockLevel = stockLevelsData ? stockLevelsData[materialId] : undefined

  const {data: recipesById} = useMaterialsRecipesById({plantId: plantCode})
  const {data: assetsById} = useHistoryAssetsByIdQuery({
    plantId: plantCode,
    timeFrame: planTimeFrame,
    throwOnError: false
  })
  const {data: assets} = useHistoryAssetsQuery({
    plantId: plantCode,
    timeFrame: planTimeFrame,
    throwOnError: false
  })
  const {data: storageData} = useMaterialStorageDataFromHistoryQuery({
    plantId: plantCode,
    materialId,
    timeFrame: planTimeFrame
  })

  const storageCapacity = storageData?.storageCapacity ?? 0

  const millETA = useMemo(() => {
    if (!recipesById || !assetsById || !assets || stockLevel === undefined) {
      return undefined
    }
    return getMillProductionTimeForMaterial({
      assetsById,
      assets,
      tonsOfMaterial: Math.max(0, storageCapacity - stockLevel),
      recipesById,
      materialId
    })
      .map(
        (production) =>
          `${production.asset_name}: ${Math.ceil(production.time).toLocaleString(language)}h`
      )
      .join(' ')
  }, [materialId, assetsById, assets, stockLevel, storageCapacity, recipesById, language])

  return <>{millETA}</>
}
